<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';

import SummaryCards from './components/SummaryCards.vue';
import SentimentChart from './components/SentimentChart.vue';
import EngagementMetrics from './components/EngagementMetrics.vue';
import CompetitorComparison from './components/CompetitorComparison.vue';
import TopMentioners from './components/TopMentioners.vue';
import MentionsFeed from './components/MentionsFeed.vue';
import PoliticalInsights from './components/PoliticalInsights.vue';
import PublicReaction from './components/PublicReaction.vue';
import TopicsChart from './components/TopicsChart.vue';
import ReportGenerator from './components/ReportGenerator.vue';
import ReportViewer from './components/ReportViewer.vue';
import CommentClusters from './components/CommentClusters.vue';
import AlertsPanel from './components/AlertsPanel.vue';
import ContentBreakdown from './components/ContentBreakdown.vue';
import ConversionIndices from './components/ConversionIndices.vue';
import NarrativesPanel from './components/NarrativesPanel.vue';
import EmotionBreakdown from './components/EmotionBreakdown.vue';
import ContentSuggestions from './components/ContentSuggestions.vue';
import ResponseLibrary from './components/ResponseLibrary.vue';
import CopilotFloatingButton from './components/CopilotFloatingButton.vue';
import CopilotPanel from './components/CopilotPanel.vue';
import TerritorialHeatmap from './components/TerritorialHeatmap.vue';
import DemographicBreakdown from './components/DemographicBreakdown.vue';
import SegmentationPanel from './components/SegmentationPanel.vue';
import FilterSelect from './components/FilterSelect.vue';
import { roleLabel } from './utils/politicalRoles.js';
import { CONTENT_CATEGORIES } from './utils/contentCategories.js';
import { AUTHOR_CLASSIFICATIONS } from './utils/authorClassifications.js';
import { EMOTIONS } from './utils/emotions.js';
import { SENTIMENT_TARGETS } from './utils/sentimentTargets.js';

const store = useStore();

const days = ref(30);
const profileId = ref('');
const platform = ref('');
const sentimentFilter = ref('');
const topicFilter = ref('');
const categoryFilter = ref('');
const authorClassFilter = ref('');
const emotionFilter = ref('');
const targetFilter = ref('');
const page = ref(1);
const mentionsSortBy = ref('recent');

const PERIODS = [
  { value: 7, label: '7d' },
  { value: 30, label: '30d' },
  { value: 90, label: '90d' },
];

const PLATFORMS = [
  { value: '', label: 'Todas as plataformas', icon: 'i-lucide-globe' },
  { value: 'facebook', label: 'Facebook', icon: 'i-lucide-facebook' },
  { value: 'instagram', label: 'Instagram', icon: 'i-lucide-instagram' },
  { value: 'tiktok', label: 'TikTok', icon: 'i-lucide-music' },
  { value: 'twitter', label: 'Twitter/X', icon: 'i-lucide-twitter' },
];

const SENTIMENTS = [
  { value: '', label: 'Todos os sentimentos', dot: 'bg-slate-400' },
  { value: 'positive', label: 'Positivo', dot: 'bg-emerald-500' },
  { value: 'negative', label: 'Negativo', dot: 'bg-rose-500' },
  { value: 'neutral', label: 'Neutro', dot: 'bg-slate-400' },
];

const summary = computed(() => store.getters['socialMonitoring/getSummary']);
const sentiment = computed(
  () => store.getters['socialMonitoring/getSentiment']
);
const engagement = computed(
  () => store.getters['socialMonitoring/getEngagement']
);
const competitors = computed(
  () => store.getters['socialMonitoring/getCompetitors']
);
const topMentioners = computed(
  () => store.getters['socialMonitoring/getTopMentioners']
);
const mentions = computed(() => store.getters['socialMonitoring/getMentions']);
const profiles = computed(() => store.getters['socialMonitoring/getProfiles']);
const insights = computed(
  () => store.getters['socialMonitoring/getPoliticalInsights']
);
const reaction = computed(
  () => store.getters['socialMonitoring/getPublicReaction']
);
const reactionAnalysis = computed(
  () => store.getters['socialMonitoring/getPublicReactionAnalysis']
);
const topics = computed(() => store.getters['socialMonitoring/getTopics']);
const clusters = computed(() => store.getters['socialMonitoring/getClusters']);
const contentBreakdown = computed(
  () => store.getters['socialMonitoring/getContentBreakdown']
);
const conversionIndices = computed(
  () => store.getters['socialMonitoring/getConversionIndices']
);
const narratives = computed(
  () => store.getters['socialMonitoring/getNarratives']
);
const territorialBreakdown = computed(
  () => store.getters['socialMonitoring/getTerritorialBreakdown']
);
const demographicBreakdown = computed(
  () => store.getters['socialMonitoring/getDemographicBreakdown']
);
const territorialSegmentation = computed(
  () => store.getters['socialMonitoring/getTerritorialSegmentation']
);
const emotionBreakdown = computed(
  () => store.getters['socialMonitoring/getEmotionBreakdown']
);
const contentSuggestions = computed(
  () => store.getters['socialMonitoring/getContentSuggestions']
);
const responseLibrary = computed(
  () => store.getters['socialMonitoring/getResponseLibrary']
);
const copilot = computed(() => store.getters['socialMonitoring/getCopilot']);
const alerts = computed(() => store.getters['socialMonitoring/getAlerts']);
const alertsUnreadCount = computed(
  () => store.getters['socialMonitoring/getAlertsUnreadCount']
);
const reports = computed(() => store.getters['socialMonitoring/getReports']);
const currentReport = computed(
  () => store.getters['socialMonitoring/getCurrentReport']
);
const uiFlags = computed(() => store.getters['socialMonitoring/getUiFlags']);

const reportOpen = ref(false);
const alertsOpen = ref(false);
const copilotOpen = ref(false);

const VIEWS = [
  { value: 'overview', label: 'Visão Geral', icon: 'i-lucide-layout-dashboard' },
  { value: 'reputation', label: 'Reputação', icon: 'i-lucide-shield-check' },
  { value: 'growth', label: 'Crescimento', icon: 'i-lucide-trending-up' },
  { value: 'electoral', label: 'Eleitoral', icon: 'i-lucide-vote' },
  { value: 'content', label: 'Conteúdo', icon: 'i-lucide-pencil-line' },
  { value: 'competition', label: 'Concorrência', icon: 'i-lucide-swords' },
];
const activeView = ref('overview');
const SECTION_VIEWS = {
  cards: ['reputation', 'growth', 'electoral', 'content', 'competition'],
  conversion: ['electoral'],
  sentiment: ['reputation', 'growth'],
  publicReaction: ['reputation'],
  topics: ['content', 'reputation'],
  insights: ['electoral', 'reputation'],
  clusters: ['reputation', 'electoral'],
  narratives: ['reputation', 'electoral'],
  contentBreakdown: ['content'],
  emotion: ['reputation'],
  contentSuggestions: ['content', 'electoral'],
  responseLibrary: ['content'],
  engagement: ['growth'],
  topMentioners: ['growth', 'electoral'],
  competitors: ['competition'],
  reports: ['electoral', 'competition'],
  feed: ['reputation', 'growth', 'content'],
  territorial: ['electoral', 'reputation'],
  demographic: ['electoral', 'growth'],
  segmentation: ['electoral'],
};
const showInView = section => {
  if (activeView.value === 'overview') return true;
  return SECTION_VIEWS[section]?.includes(activeView.value);
};
const isCollecting = ref(false);

const baseParams = computed(() => ({
  days: days.value,
  profileId: profileId.value || undefined,
  platform: platform.value || undefined,
  sentiment: sentimentFilter.value || undefined,
  topic: topicFilter.value || undefined,
  category: categoryFilter.value || undefined,
  authorClass: authorClassFilter.value || undefined,
  emotion: emotionFilter.value || undefined,
  sentimentTarget: targetFilter.value || undefined,
}));

const availableTopics = computed(() => {
  const list = topics.value || [];
  return list
    .slice(0, 20)
    .map(t => ({ value: t.topic, label: t.topic, hint: `${t.count} menções`, icon: 'i-lucide-tag' }));
});

const profileOptions = computed(() => [
  { value: '', label: 'Todos os perfis', icon: 'i-lucide-users' },
  ...profiles.value.map(p => ({
    value: p.id,
    label: p.name,
    hint: p.politicalRole ? roleLabel(p.politicalRole) : undefined,
    icon: 'i-lucide-user',
  })),
]);

const topicsOptions = computed(() => [
  { value: '', label: 'Todos os temas' },
  ...availableTopics.value,
]);

const categoryOptions = computed(() => [
  { value: '', label: 'Todas as categorias' },
  ...CONTENT_CATEGORIES.map(c => ({ value: c.value, label: c.label, dot: c.dot })),
]);

const authorClassOptions = computed(() => [
  { value: '', label: 'Todos os autores' },
  ...AUTHOR_CLASSIFICATIONS.map(a => ({ value: a.value, label: a.label, icon: a.icon })),
]);

const emotionOptions = computed(() => [
  { value: '', label: 'Todas as emoções' },
  ...EMOTIONS.map(e => ({ value: e.value, label: e.label, dot: e.dot, icon: e.icon })),
]);

const targetOptions = computed(() => [
  { value: '', label: 'Todos os alvos' },
  ...SENTIMENT_TARGETS.map(t => ({ value: t.value, label: t.label })),
]);

const hasActiveFilters = computed(
  () =>
    !!sentimentFilter.value ||
    !!topicFilter.value ||
    !!categoryFilter.value ||
    !!authorClassFilter.value ||
    !!emotionFilter.value ||
    !!targetFilter.value ||
    !!profileId.value ||
    !!platform.value
);

function clearFilters() {
  sentimentFilter.value = '';
  topicFilter.value = '';
  categoryFilter.value = '';
  authorClassFilter.value = '';
  emotionFilter.value = '';
  targetFilter.value = '';
  profileId.value = '';
  platform.value = '';
}

const selectedProfile = computed(() =>
  profileId.value
    ? profiles.value.find(p => String(p.id) === String(profileId.value))
    : null
);

const reportContext = computed(() => ({
  summary: summary.value,
  sentiment: sentiment.value,
  topics: topics.value,
  insights: insights.value,
  competitors: competitors.value,
  engagement: engagement.value,
  topMentioners: topMentioners.value,
  publicReaction: reaction.value,
  period: days.value,
  platform: platform.value || 'Todas',
}));

async function fetchAll() {
  const p = baseParams.value;
  await Promise.all([
    store.dispatch('socialMonitoring/fetchSummary', p),
    store.dispatch('socialMonitoring/fetchSentiment', p),
    store.dispatch('socialMonitoring/fetchEngagement', p),
    store.dispatch('socialMonitoring/fetchCompetitors', p),
    store.dispatch('socialMonitoring/fetchTopMentioners', p),
    store.dispatch('socialMonitoring/fetchMentions', {
      ...p,
      page: page.value,
      sortBy: mentionsSortBy.value,
    }),
    store.dispatch('socialMonitoring/fetchPublicReaction', p),
    store.dispatch('socialMonitoring/fetchTopics', p),
    store.dispatch('socialMonitoring/fetchContentBreakdown', p),
    store.dispatch('socialMonitoring/fetchConversionIndices', p),
    store.dispatch('socialMonitoring/fetchEmotionBreakdown', p),
    store.dispatch('socialMonitoring/fetchTerritorialBreakdown', p),
    store.dispatch('socialMonitoring/fetchDemographicBreakdown', p),
    store.dispatch('socialMonitoring/fetchTerritorialSegmentation', p),
    store.dispatch('socialMonitoring/listReports', {
      profileId: profileId.value || undefined,
    }),
  ]);
}

async function refreshInsights() {
  if (!profileId.value) return;
  await store.dispatch('socialMonitoring/fetchPoliticalInsights', {
    ...baseParams.value,
    refresh: true,
  });
}

async function openAlerts() {
  alertsOpen.value = true;
  await store.dispatch('socialMonitoring/fetchAlerts');
}

async function acknowledgeAlert(id) {
  await store.dispatch('socialMonitoring/acknowledgeAlert', id);
}

async function acknowledgeAllAlerts() {
  await store.dispatch('socialMonitoring/acknowledgeAllAlerts');
}

async function refreshClusters() {
  await store.dispatch('socialMonitoring/fetchClusters', {
    ...baseParams.value,
    refresh: true,
  });
}

async function refreshNarratives() {
  await store.dispatch('socialMonitoring/fetchNarratives', {
    ...baseParams.value,
    refresh: true,
  });
}

async function refreshContentSuggestions() {
  if (!profileId.value) return;
  await store.dispatch('socialMonitoring/fetchContentSuggestions', {
    ...baseParams.value,
    refresh: true,
  });
}

async function refreshResponseLibrary() {
  if (!profileId.value) return;
  await store.dispatch('socialMonitoring/fetchResponseLibrary', {
    ...baseParams.value,
    refresh: true,
  });
}

async function openCopilot() {
  copilotOpen.value = true;
  if (!profileId.value) return;
  await store.dispatch('socialMonitoring/fetchCopilotLatest', baseParams.value);
}

async function runCopilot() {
  if (!profileId.value) return;
  await store.dispatch('socialMonitoring/runCopilot', baseParams.value);
}

async function analyzeReaction() {
  if (!profileId.value) return;
  await store.dispatch(
    'socialMonitoring/fetchPublicReactionAnalysis',
    baseParams.value
  );
}

async function triggerCollect() {
  try {
    isCollecting.value = true;
    await store.dispatch('socialMonitoring/collectAll');
    useAlert('Coleta enfileirada. Os dados vão aparecer em instantes.');
  } catch (e) {
    useAlert(e.message || 'Falha ao iniciar coleta');
  } finally {
    isCollecting.value = false;
  }
}

async function generateReport() {
  if (!profileId.value) return;
  try {
    await store.dispatch('socialMonitoring/generateReport', baseParams.value);
    reportOpen.value = true;
    await store.dispatch('socialMonitoring/listReports', {
      profileId: profileId.value,
    });
  } catch (e) {
    useAlert(e.message || 'Falha ao gerar relatório');
  }
}

async function viewReport(id) {
  await store.dispatch('socialMonitoring/getReport', id);
  reportOpen.value = true;
}

function changeMentionsPage(p) {
  page.value = p;
  store.dispatch('socialMonitoring/fetchMentions', {
    ...baseParams.value,
    page: p,
    sortBy: mentionsSortBy.value,
  });
}

function changeMentionsSort(value) {
  mentionsSortBy.value = value;
  page.value = 1;
  store.dispatch('socialMonitoring/fetchMentions', {
    ...baseParams.value,
    page: 1,
    sortBy: value,
  });
}

watch([days, profileId, platform, sentimentFilter, topicFilter, categoryFilter, authorClassFilter, emotionFilter, targetFilter], () => {
  page.value = 1;
  fetchAll();
});

onMounted(async () => {
  await store.dispatch('socialMonitoring/fetchProfiles');
  fetchAll();
  store.dispatch('socialMonitoring/fetchAlerts');
});
</script>

<template>
  <div class="flex h-full w-full flex-col overflow-hidden bg-n-background">
    <!-- Header -->
    <div class="sticky top-0 z-10 border-b border-n-strong/40 bg-n-background shadow-sm">
      <!-- Row 1: identity + actions + alerts -->
      <div class="flex flex-wrap items-center gap-3 px-5 pt-4 pb-3">
        <div class="flex items-center gap-2.5">
          <div class="flex size-9 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 shadow-sm">
            <span class="i-lucide-radio-tower size-5 text-white" />
          </div>
          <div>
            <h1 class="text-base font-bold leading-tight text-n-slate-12">Redes Sociais</h1>
            <p class="text-[11px] text-n-slate-11">Monitoramento e inteligência política</p>
          </div>
        </div>

        <div class="ml-auto flex items-center gap-2">
          <button
            type="button"
            class="relative inline-flex h-9 w-9 items-center justify-center rounded-lg border border-n-strong/30 bg-n-background transition hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700"
            title="Alertas"
            @click="openAlerts"
          >
            <span class="i-lucide-bell size-4 text-n-slate-12" />
            <span
              v-if="alertsUnreadCount > 0"
              class="absolute -right-1 -top-1 inline-flex min-w-[18px] items-center justify-center rounded-full bg-rose-600 px-1 text-[10px] font-bold text-white shadow ring-2 ring-n-background"
            >
              {{ alertsUnreadCount > 99 ? '99+' : alertsUnreadCount }}
            </span>
          </button>

          <Button
            variant="outline"
            size="sm"
            icon="i-lucide-refresh-cw"
            @click="fetchAll"
          >
            Atualizar
          </Button>
          <Button
            variant="solid"
            color="blue"
            size="sm"
            icon="i-lucide-download-cloud"
            :is-loading="isCollecting"
            @click="triggerCollect"
          >
            Coletar
          </Button>
        </div>
      </div>

      <!-- Row 2: filters bar -->
      <div class="flex flex-wrap items-center gap-2 border-t border-n-weak/60 bg-n-background px-5 py-3">
        <!-- Period pills -->
        <div class="inline-flex h-9 items-center rounded-lg border border-n-strong/40 bg-n-background p-1">
          <span class="i-lucide-calendar-days mx-2 size-3.5 text-n-slate-11" />
          <button
            v-for="p in PERIODS"
            :key="p.value"
            type="button"
            class="rounded-md px-2.5 py-1 text-xs font-semibold leading-none transition"
            :class="
              days === p.value
                ? 'bg-n-slate-12 text-n-background shadow-sm'
                : 'text-n-slate-11 hover:text-n-slate-12'
            "
            @click="days = p.value"
          >
            {{ p.label }}
          </button>
        </div>

        <FilterSelect
          v-model="profileId"
          :options="profileOptions"
          icon="i-lucide-user"
          placeholder="Perfil"
          min-width="180px"
        />

        <FilterSelect
          v-model="platform"
          :options="PLATFORMS"
          icon="i-lucide-globe"
          placeholder="Plataforma"
        />

        <span class="hidden h-6 w-px bg-n-weak/60 md:block" />

        <FilterSelect
          v-model="sentimentFilter"
          :options="SENTIMENTS"
          icon="i-lucide-smile"
          placeholder="Sentimento"
        />

        <FilterSelect
          v-if="availableTopics.length"
          v-model="topicFilter"
          :options="topicsOptions"
          icon="i-lucide-tag"
          placeholder="Tema"
        />

        <FilterSelect
          v-model="categoryFilter"
          :options="categoryOptions"
          icon="i-lucide-layers"
          placeholder="Categoria"
        />

        <FilterSelect
          v-model="authorClassFilter"
          :options="authorClassOptions"
          icon="i-lucide-users"
          placeholder="Autor"
        />

        <FilterSelect
          v-model="emotionFilter"
          :options="emotionOptions"
          icon="i-lucide-heart"
          placeholder="Emoção"
        />

        <FilterSelect
          v-model="targetFilter"
          :options="targetOptions"
          icon="i-lucide-target"
          placeholder="Alvo"
        />

        <!-- Active filters indicator -->
        <button
          v-if="hasActiveFilters"
          type="button"
          class="ml-auto inline-flex h-9 items-center gap-1.5 rounded-lg border border-n-strong/40 bg-n-background px-3 text-xs font-semibold text-n-slate-12 hover:bg-n-slate-2"
          @click="clearFilters"
        >
          <span class="i-lucide-x size-3.5" />
          Limpar filtros
        </button>
      </div>
    </div>

    <!-- View tabs -->
    <div class="border-b border-n-weak/60 bg-n-background px-5">
      <nav class="-mb-px flex flex-wrap gap-1 overflow-x-auto">
        <button
          v-for="v in VIEWS"
          :key="v.value"
          type="button"
          class="inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-xs font-semibold transition"
          :class="
            activeView === v.value
              ? 'border-blue-600 text-blue-700'
              : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
          "
          @click="activeView = v.value"
        >
          <span :class="v.icon" class="size-3.5" />
          {{ v.label }}
        </button>
      </nav>
    </div>

    <!-- Content -->
    <div class="flex-1 space-y-4 overflow-y-auto p-4">
      <SummaryCards v-if="showInView('cards')" :summary="summary" />

      <ConversionIndices v-if="showInView('conversion')" :data="conversionIndices" />

      <SentimentChart v-if="showInView('sentiment')" :data="sentiment" />

      <div v-if="showInView('publicReaction') || showInView('topics')" class="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <PublicReaction
          v-if="showInView('publicReaction')"
          :metrics="reaction"
          :analysis="reactionAnalysis"
          :analysis-loading="uiFlags.isFetchingReactionAnalysis"
          :analysis-disabled="!profileId"
          @analyze="analyzeReaction"
        />
        <TopicsChart v-if="showInView('topics')" :topics="topics" />
      </div>

      <PoliticalInsights
        v-if="showInView('insights')"
        :insights="insights"
        :loading="uiFlags.isFetchingInsights"
        :disabled="!profileId"
        @refresh="refreshInsights"
      />

      <CommentClusters
        v-if="showInView('clusters')"
        :data="clusters"
        :loading="uiFlags.isFetchingClusters"
        :disabled="!profileId"
        @refresh="refreshClusters"
      />

      <NarrativesPanel
        v-if="showInView('narratives')"
        :data="narratives"
        :loading="uiFlags.isFetchingNarratives"
        :disabled="!profileId"
        @refresh="refreshNarratives"
      />

      <ContentBreakdown v-if="showInView('contentBreakdown')" :data="contentBreakdown" />

      <EmotionBreakdown v-if="showInView('emotion')" :data="emotionBreakdown" />

      <TerritorialHeatmap v-if="showInView('territorial')" :items="territorialBreakdown" />

      <DemographicBreakdown v-if="showInView('demographic')" :data="demographicBreakdown" />

      <SegmentationPanel v-if="showInView('segmentation')" :data="territorialSegmentation" />

      <ContentSuggestions
        v-if="showInView('contentSuggestions')"
        :data="contentSuggestions"
        :loading="uiFlags.isFetchingSuggestions"
        :disabled="!profileId"
        @refresh="refreshContentSuggestions"
      />

      <ResponseLibrary
        v-if="showInView('responseLibrary')"
        :data="responseLibrary"
        :loading="uiFlags.isFetchingResponseLibrary"
        :disabled="!profileId"
        @refresh="refreshResponseLibrary"
      />

      <div v-if="showInView('engagement') || showInView('topMentioners')" class="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <EngagementMetrics v-if="showInView('engagement')" :engagement="engagement" />
        <TopMentioners v-if="showInView('topMentioners')" :mentioners="topMentioners" />
      </div>

      <div v-if="showInView('competitors') || showInView('reports')" class="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div v-if="showInView('competitors')" class="lg:col-span-2">
          <CompetitorComparison :competitors="competitors" />
        </div>
        <ReportGenerator
          v-if="showInView('reports')"
          :reports="reports"
          :loading="uiFlags.isGeneratingReport"
          :disabled="!profileId"
          @generate="generateReport"
          @view="viewReport"
        />
      </div>

      <MentionsFeed
        v-if="showInView('feed')"
        :mentions="mentions"
        :sort-by="mentionsSortBy"
        @change-page="changeMentionsPage"
        @change-sort="changeMentionsSort"
      />
    </div>

    <ReportViewer
      :report="currentReport"
      :open="reportOpen"
      :profile="selectedProfile"
      :context="reportContext"
      @close="reportOpen = false"
    />

    <AlertsPanel
      :open="alertsOpen"
      :alerts="alerts"
      :unread-count="alertsUnreadCount"
      @close="alertsOpen = false"
      @acknowledge="acknowledgeAlert"
      @acknowledge-all="acknowledgeAllAlerts"
    />

    <CopilotFloatingButton
      :count="copilot.actions?.length || 0"
      @open="openCopilot"
    />

    <CopilotPanel
      :open="copilotOpen"
      :data="copilot"
      :loading="uiFlags.isFetchingCopilot"
      :running="uiFlags.isRunningCopilot"
      :disabled="!profileId"
      @close="copilotOpen = false"
      @run="runCopilot"
    />
  </div>
</template>

